import { Box, CircularProgress } from "@mui/material";
import { type CaseOut } from "@platform/api/golden";
import { Alert, ErrorBoundary, Typography } from "@platform/design-system";
import { useParams } from "react-router";

import { CaseStateChip } from "../cases/CaseStateChip";
import { usePageTitle } from "../hooks/usePageTitle";
import { RfpLink } from "../shared/RfpLink";

import { AuditSample } from "./AuditSample";
import { CaseActions } from "./CaseActions";
import { CaseCorrections } from "./CaseCorrections";
import { CaseDocuments } from "./CaseDocuments";
import { CaseIntake } from "./CaseIntake";
import { CaseLabelForm } from "./CaseLabelForm";
import { CaseLatestRun } from "./CaseLatestRun";
import { useCase, useImportStatus } from "./caseQueries";
import { CaseRuns } from "./CaseRuns";
import { SectionStatus } from "./SectionStatus";

import type { ReactNode } from "react";

export const CASE_PAGE_TEST_ID = "golden-case";

const CASE_ID = /^[1-9][0-9]*$/;

const Fact = ({
  term,
  children,
}: {
  readonly term: string;
  readonly children: ReactNode;
}): ReactNode => (
  <>
    <Box component="dt">
      <Typography variant="body-medium" color="textSecondary" component="span">
        {term}
      </Typography>
    </Box>
    <Box component="dd" sx={{ m: 0 }}>
      <Typography variant="body-medium" component="span">
        {children}
      </Typography>
    </Box>
  </>
);

const CaseHeading = ({
  goldenCase,
}: {
  readonly goldenCase: CaseOut;
}): ReactNode => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
    <Typography variant="header-large" component="h1">
      Case #{goldenCase.id}
    </Typography>
    <CaseStateChip state={goldenCase.state} />
  </Box>
);

/** The case while its documents import, in place of its sections, which read what the import brings. */
const CaseImporting = ({
  goldenCase,
}: {
  readonly goldenCase: CaseOut;
}): ReactNode => (
  <>
    <CaseHeading goldenCase={goldenCase} />
    <Alert
      data-testid={`${CASE_PAGE_TEST_ID}-importing`}
      severity="info"
      icon={<CircularProgress size={20} aria-label="Importing" />}
    >
      Importing the case&apos;s documents from production SharePoint and copying
      them to the RFP&apos;s folder in preprod SharePoint. The case opens here
      when the import finishes.
    </Alert>
  </>
);

const CaseSections = ({
  goldenCase,
}: {
  readonly goldenCase: CaseOut;
}): ReactNode => (
  <>
    <CaseHeading goldenCase={goldenCase} />
    <Box
      component="dl"
      data-testid={`${CASE_PAGE_TEST_ID}-facts`}
      sx={{
        display: "grid",
        gridTemplateColumns: "max-content 1fr",
        columnGap: 3,
        rowGap: 1,
        m: 0,
      }}
    >
      <Fact term="Source RFP">
        <RfpLink
          id={goldenCase.source_rfp_id}
          url={goldenCase.source_rfp_url}
        />
      </Fact>
      <Fact term="SharePoint folder">{goldenCase.source_folder ?? "—"}</Fact>
      <Fact term="Final documents">
        {goldenCase.use_final_documents
          ? "Used in place of the broker's documents"
          : "Not used"}
      </Fact>
      <Fact term="Correction revision">{goldenCase.correction_revision}</Fact>
    </Box>
    <ErrorBoundary>
      <CaseActions goldenCase={goldenCase} />
    </ErrorBoundary>
    <ErrorBoundary>
      <CaseLabelForm goldenCase={goldenCase} />
    </ErrorBoundary>
    <ErrorBoundary>
      <CaseLatestRun caseId={goldenCase.id} />
    </ErrorBoundary>
    <ErrorBoundary>
      <CaseRuns caseId={goldenCase.id} />
    </ErrorBoundary>
    <ErrorBoundary>
      <CaseDocuments caseId={goldenCase.id} />
    </ErrorBoundary>
    <ErrorBoundary>
      <CaseIntake
        caseId={goldenCase.id}
        hasBaseline={goldenCase.state !== "awaiting_refresh"}
      />
    </ErrorBoundary>
    <ErrorBoundary>
      <AuditSample caseId={goldenCase.id} />
    </ErrorBoundary>
    <ErrorBoundary>
      <CaseCorrections caseId={goldenCase.id} />
    </ErrorBoundary>
  </>
);

const LoadedCase = ({ caseId }: { readonly caseId: number }): ReactNode => {
  const goldenCase = useCase(caseId);
  const importStatus = useImportStatus(caseId);
  if (!goldenCase.isSuccess || importStatus.isPending) {
    return (
      <SectionStatus
        testId={CASE_PAGE_TEST_ID}
        label="case"
        error={goldenCase.error}
      />
    );
  }
  // An import whose state cannot be read leaves the case open: its sections still work.
  // A failed poll keeps the last answer as data, so the error is checked first.
  const importing = !importStatus.isError && importStatus.data?.running;
  return importing ? (
    <CaseImporting goldenCase={goldenCase.data} />
  ) : (
    // Keyed by case, so moving to another case starts every section's own state over.
    <CaseSections key={goldenCase.data.id} goldenCase={goldenCase.data} />
  );
};

/** One golden case: its facts and label, what to do with it, and everything reviewed on it. */
export const CasePage = (): ReactNode => {
  const { caseId = "" } = useParams();
  usePageTitle(`Case ${caseId}`);

  return (
    <Box
      data-testid={CASE_PAGE_TEST_ID}
      sx={{ display: "flex", flexDirection: "column", gap: 3 }}
    >
      {CASE_ID.test(caseId) ? (
        <LoadedCase caseId={Number(caseId)} />
      ) : (
        <Alert
          data-testid={`${CASE_PAGE_TEST_ID}-not-found`}
          severity="warning"
        >
          There is no case {caseId}.
        </Alert>
      )}
    </Box>
  );
};
